import React from "react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star } from "lucide-react";

const stageColors = {
  new: "bg-blue-500",
  screening: "bg-amber-500",
  interview: "bg-violet-500",
  technical: "bg-indigo-500",
  offer: "bg-emerald-500",
  hired: "bg-green-500",
  rejected: "bg-slate-400",
};

export default function PipelineColumn({
  stage,
  applications,
  candidates,
  jobs,
  onCardClick,
}) {
  const getCandidate = (id) => candidates.find((c) => c.id === id);
  const getJob = (id) => jobs.find((j) => j.id === id);

  return (
    <div className="flex-shrink-0 w-80">
      <div className="bg-slate-50 rounded-2xl p-4">

        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className={`w-2 h-2 rounded-full ${stageColors[stage.key]}`} />
          <h3 className="font-semibold text-slate-900">{stage.label}</h3>
          <Badge className="bg-white border text-xs">{applications.length}</Badge>
        </div>

        {/* Droppable Area */}
        <Droppable droppableId={stage.key}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`min-h-[200px] space-y-3 p-2 rounded-xl transition-colors 
              ${snapshot.isDraggingOver ? "bg-indigo-50" : ""}`}
            >
              {applications.map((app, index) => {
                const candidate = getCandidate(app.candidate_id);
                const job = getJob(app.job_id);

                if (!candidate) return null;

                const initials = candidate.full_name
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase();

                return (
                  <Draggable key={app.id} draggableId={app.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        onClick={() => onCardClick(app)}
                        className={`bg-white rounded-xl p-4 border shadow-sm hover:shadow-md cursor-pointer 
                        transition-all duration-200 ${
                          snapshot.isDragging ? "shadow-lg scale-[1.02]" : ""
                        }`}
                      >
                        {/* Avatar + Info */}
                        <div className="flex items-start gap-3">
                          <Avatar className="h-10 w-10 border">
                            {/* Force fallback when no photo exists */}
                            {candidate.photo_url ? (
                              <AvatarImage src={candidate.photo_url} />
                            ) : (
                              <AvatarFallback className="bg-indigo-600 text-white">
                                {initials}
                              </AvatarFallback>
                            )}
                          </Avatar>

                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {candidate.full_name}
                            </p>
                            {job && (
                              <p className="text-xs text-slate-500 truncate">
                                {job.title}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Match Score Bar */}
                        {app.match_score && (
                          <div className="flex items-center gap-2 mt-3">
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-indigo-500 to-violet-500"
                                style={{ width: `${app.match_score}%` }}
                              />
                            </div>
                            <span className="text-xs font-semibold text-slate-700">
                              {app.match_score}%
                            </span>
                          </div>
                        )}

                        {/* Rating + Skills */}
                        <div className="flex items-center justify-between mt-3">
                          {candidate.rating && (
                            <div className="flex items-center gap-1 text-amber-500">
                              <Star className="w-3 h-3 fill-current" />
                              <span className="text-xs font-medium">
                                {candidate.rating}
                              </span>
                            </div>
                          )}

                          {candidate.skills?.length > 0 && (
                            <div className="flex gap-1">
                              {candidate.skills.slice(0, 2).map((skill, i) => (
                                <Badge
                                  key={i}
                                  className="text-[10px] px-1.5 bg-slate-100"
                                >
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </div>
  );
}
